import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { t } from "../../i18n";

export default function Documentation() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const highlightsRef = useRef([]);

  const sections = [
    { id: "overview", title: t("Overview") },
    { id: "creating-agent", title: t("Creating Your AI Agent") },
    { id: "managing-agents", title: t("Managing Your Agents") },
    { id: "inbox", title: t("Using the Inbox") },
    { id: "installing-widget", title: t("Installing the Chat Widget") },
    { id: "platforms", title: t("Platform-Specific Installation") },
    { id: "testing", title: t("Testing & Troubleshooting") },
  ];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Применяем поиск после рендера
  useEffect(() => {
    if (!searchQuery.trim()) {
      // Удаляем все подсветки при очистке поиска
      const allHighlights = document.querySelectorAll(".search-highlight");
      allHighlights.forEach((el) => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent), el);
          parent.normalize();
        }
      });
      setSearchResults(null);
      highlightsRef.current = [];
      setCurrentMatchIndex(0);
      return;
    }

    const searchTerm = searchQuery.toLowerCase();
    const allSections = document.querySelectorAll(".doc-section");
    let firstFound = null;
    let matchCount = 0;
    highlightsRef.current = []; // Сбрасываем массив подсветок

    // Удаляем предыдущие подсветки
    const allHighlights = document.querySelectorAll(".search-highlight");
    allHighlights.forEach((el) => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent), el);
        parent.normalize();
      }
    });

    allSections.forEach((section) => {
      const walker = document.createTreeWalker(
        section,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.toLowerCase().includes(searchTerm)) {
          textNodes.push(node);
        }
      }

      if (textNodes.length > 0) {
        // Подсвечиваем найденный текст (все совпадения)
        textNodes.forEach((textNode) => {
          const text = textNode.textContent;
          const lowerText = text.toLowerCase();
          const indices = [];
          let index = lowerText.indexOf(searchTerm);

          // Находим все совпадения
          while (index !== -1) {
            indices.push(index);
            index = lowerText.indexOf(searchTerm, index + 1);
          }

          // Увеличиваем счетчик совпадений
          matchCount += indices.length;

          if (indices.length > 0) {
            // Создаем фрагмент с подсветками
            const fragment = document.createDocumentFragment();
            let lastIndex = 0;

            indices.forEach((idx) => {
              // Добавляем текст до совпадения
              if (idx > lastIndex) {
                fragment.appendChild(
                  document.createTextNode(text.substring(lastIndex, idx))
                );
              }

              // Добавляем подсвеченное совпадение
              const highlight = document.createElement("mark");
              highlight.className = "search-highlight";
              highlight.style.backgroundColor = "#fff3cd";
              highlight.style.padding = "2px 0";
              highlight.style.borderRadius = "2px";
              highlight.textContent = text.substring(
                idx,
                idx + searchQuery.length
              );
              fragment.appendChild(highlight);

              // Сохраняем ссылку на подсветку
              highlightsRef.current.push(highlight);

              lastIndex = idx + searchQuery.length;
            });

            // Добавляем оставшийся текст
            if (lastIndex < text.length) {
              fragment.appendChild(
                document.createTextNode(text.substring(lastIndex))
              );
            }

            // Заменяем текстовый узел на фрагмент
            const parent = textNode.parentNode;
            if (parent) {
              parent.replaceChild(fragment, textNode);
            }
          }
        });

        // Прокручиваем к первой найденной секции
        if (!firstFound) {
          firstFound = section;
        }
      }
    });

    // Прокручиваем к первому совпадению
    if (firstFound && highlightsRef.current.length > 0) {
      setTimeout(() => {
        // Прокручиваем к первому результату и подсвечиваем его
        const firstHighlight = highlightsRef.current[0];
        if (firstHighlight) {
          highlightsRef.current.forEach((hl, idx) => {
            if (idx === 0) {
              hl.style.backgroundColor = "#ffc107";
              hl.style.fontWeight = "bold";
            } else {
              hl.style.backgroundColor = "#fff3cd";
              hl.style.fontWeight = "normal";
            }
          });
          firstHighlight.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          firstFound.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }

    setSearchResults(matchCount > 0 ? { count: matchCount } : null);
    setCurrentMatchIndex(0); // Сбрасываем индекс при новом поиске
  }, [searchQuery]);

  // Функция для перехода к следующему результату
  const scrollToNextMatch = () => {
    if (highlightsRef.current.length === 0) return;

    const nextIndex = (currentMatchIndex + 1) % highlightsRef.current.length;
    setCurrentMatchIndex(nextIndex);

    const highlight = highlightsRef.current[nextIndex];
    if (highlight) {
      // Подсвечиваем текущий результат другим цветом
      highlightsRef.current.forEach((hl, idx) => {
        if (idx === nextIndex) {
          hl.style.backgroundColor = "#ffc107";
          hl.style.fontWeight = "bold";
        } else {
          hl.style.backgroundColor = "#fff3cd";
          hl.style.fontWeight = "normal";
        }
      });

      highlight.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const location = useLocation();
  useEffect(() => {
    if (location && location.hash) {
      const id = location.hash.replace("#", "");
      // Slight delay to ensure DOM ready
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [location]);

  return (
    <div className="documentation">
      <div className="documentation-header">
        <h1>{t("📚 Supchats Personal Account Guide")}</h1>
        <p>{t("Complete guide to managing your AI assistants and chat widget")}</p>
      </div>

      {/* TOC + Content layout */}
      <div className="documentation-layout">
        {/* TOC Sidebar */}
        <aside className="documentation-sidebar">
          <div className="documentation-toc"
            style={{
              background: "#ffffff",
              border: "1px solid #e9ecef",
              borderRadius: 8,
              padding: 16,
            }}
          >
            {/* Search Input */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input
                  type="text"
                  placeholder={t("Search documentation...")}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim() && searchResults) {
                      scrollToNextMatch();
                    }
                  }}
                  style={{
                    width: "90%",
                    padding: "8px 12px",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    fontSize: 14,
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                  onBlur={(e) => (e.target.style.borderColor = "#dee2e6")}
                />
                <button
                  onClick={scrollToNextMatch}
                  disabled={!searchQuery.trim() || !searchResults}
                  style={{
                    width: "10%",
                    minWidth: 36,
                    height: "36px",
                    padding: 0,
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    background:
                      searchQuery.trim() && searchResults ? "#667eea" : "#f8f9fa",
                    color: searchQuery.trim() && searchResults ? "#fff" : "#6c757d",
                    cursor:
                      searchQuery.trim() && searchResults ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    transition: "all 0.2s",
                  }}
                  title={
                    searchQuery.trim() && searchResults
                      ? `${t("Next result")} (${currentMatchIndex + 1}/${
                          searchResults.count
                        }) - ${t("Press Enter")}`
                      : t("No results")
                  }
                  onMouseEnter={(e) => {
                    if (searchQuery.trim() && searchResults) {
                      e.target.style.background = "#5a67d8";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (searchQuery.trim() && searchResults) {
                      e.target.style.background = "#667eea";
                    }
                  }}
                >
                  🔍
                </button>
              </div>
              {searchResults && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: "#28a745",
                    fontWeight: 500,
                  }}
                >
                  {t("Matches found:")} {searchResults.count}
                </div>
              )}
              {searchQuery.trim() && !searchResults && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: "#dc3545",
                  }}
                >
                  {t("No results found")}
                </div>
              )}
            </div>

            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("On this page")}</div>
            <nav>
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollToId(s.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        color: "#667eea",
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: 14,
                      }}
                      title={`${t("Go to section")}: ${s.title}`}
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="documentation-content">
          {/* Overview */}
          <section id="overview" className="doc-section">
            <h2>{t("🎯 Overview")}</h2>
            <div className="doc-step">
              <h3>{t("Your Personal Account")}</h3>
              <p>
                {t(
                  "Welcome to your Supchats personal account! Here you can create, manage, and monitor your AI assistants. Your account includes several key sections:"
                )}
              </p>
              <ul>
                <li>
                  <strong>{t("Agents")}:</strong> {t("Create and manage your AI assistants")}
                </li>
                <li>
                  <strong>{t("Inbox")}:</strong> {t("View and respond to customer conversations")}
                </li>
                <li>
                  <strong>{t("Settings")}:</strong> {t("Manage your account preferences")}
                </li>
                {/* <li><strong>{t('Payment')}:</strong> {t('View your current plan and usage')}</li> */}
                <li>
                  <strong>{t("News")}:</strong> {t("Stay updated with latest features")}
                </li>
                <li>
                  <strong>{t("Documentation")}:</strong> {t("This comprehensive guide")}
                </li>
              </ul>
            </div>

            {/* <div className="doc-step">
              <h3>{t('Plan Limits')}</h3>
              <p>{t('Your current plan determines your capabilities:')}</p>
              <ul>
                <li><strong>{t('Free')}:</strong> {t('1 active agent, 10 messages/month')}</li>
                <li><strong>{t('Professional ($10/month)')}:</strong> {t('3 active agents, 1000 messages/month')}</li>
                <li><strong>{t('Unlimited ($50/month)')}:</strong> {t('Unlimited agents and messages')}</li>
              </ul>
            </div> */}
          </section>

          {/* Agent Creation */}
          <section id="creating-agent" className="doc-section">
            <h2>{t("🤖 Creating Your AI Agent")}</h2>
            <div className="doc-step">
              <h3>{t("Getting Started")}</h3>
              <p>
                {t(
                  'Navigate to the Agents tab and click "Add Agent" to start creating your AI assistant. The form will appear at the bottom of the page.'
                )}{" "}
                <a href="/agents" target="_blank" rel="noreferrer">
                  {t("Open the Agents page in a new tab →")}
                </a>
              </p>
            </div>

            <div className="doc-step">
              <h3>{t("Required Fields")}</h3>
              <p>{t("These fields are mandatory and must be filled out:")}</p>
              <ul>
                <li>
                  <strong>{t("Agent Name *")}:</strong>{" "}
                  {t(
                    'Choose a descriptive name for your AI assistant. Keep it under 25 characters for optimal display in dropdowns and lists. Examples: "Customer Support", "Sales Assistant", "Help Bot"'
                  )}
                </li>
                <li>
                  <strong>{t("Domain or IP *")}:</strong>{" "}
                  {t(
                    "Enter your website domain (e.g., example.com) or IP address. This helps the AI understand the context of your website and provides better responses."
                  )}
                </li>
                <li>
                  <strong>{t("Prompt *")}:</strong>{" "}
                  {t(
                    "Define the AI's role, personality, and response guidelines. This is the most important field that determines how your AI behaves."
                  )}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("AI Configuration Fields")}</h3>
              <p>{t("Configure how your AI assistant behaves and responds:")}</p>
              <ul>
                <li>
                  <strong>{t("Prompt Style")}:</strong>{" "}
                  {t(
                    'Choose between "Friendly" (casual, warm tone) or "Formal" (professional, business-like tone). This affects the overall communication style.'
                  )}
                </li>
                <li>
                  <strong>{t("Welcome Message")}:</strong>{" "}
                  {t(
                    "The initial message shown to visitors when they open the chat. Make it welcoming and informative about what you can help with."
                  )}
                </li>
                <li>
                  <strong>{t("Contact Details")}:</strong>{" "}
                  {t(
                    "Provide support contact information (email, phone, etc.) that the AI can share with customers when needed."
                  )}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Knowledge Base Fields")}</h3>
              <p>{t("Enhance your AI's knowledge with relevant information:")}</p>
              <ul>
                <li>
                  <strong>{t("Knowledge Base")}:</strong>{" "}
                  {t(
                    "Add text or links with information about your business, products, services, policies, etc. This helps the AI provide accurate information."
                  )}
                </li>
                <li>
                  <strong>{t("Training Links")}:</strong>{" "}
                  {t(
                    'Provide URLs for the AI to learn from. Use the "Generate" button to automatically discover relevant pages on your website.'
                  )}
                </li>
                <li>
                  <strong>{t("FAQ")}:</strong>{" "}
                  {t(
                    'Add frequently asked questions and answers in the format "question : answer". Use the "Generate" button to automatically create FAQ based on your domain.'
                  )}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Advanced Settings")}</h3>
              <p>{t("Fine-tune your agent's behavior with these optional settings:")}</p>
              <ul>
                <li>
                  <strong>{t("Use support style for admin messages")}:</strong>{" "}
                  {t(
                    "Enable this for customer service scenarios. When checked, messages from admins will appear in a support-style format."
                  )}
                </li>
                <li>
                  <strong>{t("Support Name")}:</strong>{" "}
                  {t(
                    'Set the name displayed in support-style messages (e.g., "Support Team", "Customer Care").'
                  )}
                </li>
                <li>
                  <strong>{t("Auto-open chat")}:</strong>{" "}
                  {t(
                    "Automatically open the chat widget after a specified delay (in seconds). Useful for engaging visitors proactively."
                  )}
                </li>
                <li>
                  <strong>{t("Auto-archive chat")}:</strong>{" "}
                  {t(
                    "Automatically archive inactive chats after a specified delay (in minutes). Helps keep your inbox organized."
                  )}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Tips for Better Agents")}</h3>
              <ul>
                <li>
                  <strong>{t("Clear Prompt")}:</strong>{" "}
                  {t(
                    'Be specific about the AI\'s role. Instead of "Help customers", use "You are a customer support representative for [Company] specializing in [products/services]."'
                  )}
                </li>
                <li>
                  <strong>{t("Relevant Knowledge")}:</strong>{" "}
                  {t(
                    "Include information about your business hours, return policy, shipping information, and common product questions."
                  )}
                </li>
                <li>
                  <strong>{t("Test Your Agent")}:</strong>{" "}
                  {t(
                    "After creating, test the agent thoroughly to ensure it responds appropriately to common questions."
                  )}
                </li>
                <li>
                  <strong>{t("Regular Updates")}:</strong>{" "}
                  {t("Keep your knowledge base and FAQ updated as your business evolves.")}
                </li>
              </ul>
            </div>
          </section>

          {/* Managing Your Agents */}
          <section id="managing-agents" className="doc-section">
            <h2>{t("⚙️ Managing Your Agents")}</h2>
            <div className="doc-step">
              <h3>{t("Agent Activation")}</h3>
              <p>{t("Control which agents are active based on system limits:")}</p>
              <ul>
                <li>
                  <strong>{t("Play/Pause Buttons")}:</strong>{" "}
                  {t(
                    "Use the green play button to activate an agent, or the yellow pause button to deactivate it."
                  )}
                </li>
                {/* <li><strong>{t('Plan Limits')}:</strong> {t('Free plan allows 1 active agent, Professional allows 3, Unlimited allows all agents to be active.')}</li> */}
                <li>
                  <strong>{t("Agent Selection")}:</strong>{" "}
                  {t(
                    "If you try to activate more agents than allowed, you'll be prompted to choose which agents to keep active."
                  )}
                </li>
                <li>
                  <strong>{t("Blocked Agents")}:</strong>{" "}
                  {t("Agents blocked by admin cannot be activated by users.")}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Agent Status Indicators")}</h3>
              <p>{t("Understand what each status means:")}</p>
              <ul>
                <li>
                  <strong>{t("Green Play Button")}:</strong>{" "}
                  {t("Agent is active and responding to messages")}
                </li>
                <li>
                  <strong>{t("Yellow Pause Button")}:</strong>{" "}
                  {t("Agent is inactive and won't respond to messages")}
                </li>
                <li>
                  <strong>{t('Red "Suspended" Banner')}:</strong>{" "}
                  {t("Agent is blocked by admin and cannot be activated")}
                </li>
                {/* <li><strong>{t('Message Limits')}:</strong> {t('Check your monthly message usage in the plan display')}</li> */}
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Editing Agents")}</h3>
              <p>{t("To modify an existing agent:")}</p>
              <ul>
                <li>{t('Click the "Edit" button next to the agent you want to modify')}</li>
                <li>{t("The edit form will appear directly under the agent card")}</li>
                <li>{t('Make your changes and click "Save" to update the agent')}</li>
                <li>{t("All changes take effect immediately")}</li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Script Generation")}</h3>
              <p>{t("Generate the installation script for your agent:")}</p>
              <ul>
                <li>{t('Click "View Script" next to your agent to generate the installation code')}</li>
                <li>{t("The script is unique to each agent and includes all configurations")}</li>
                <li>{t("Copy the script and install it on your website (see installation section below)")}</li>
                <li>{t("The script will toggle visibility on subsequent clicks")}</li>
              </ul>
            </div>
          </section>

          {/* Inbox Usage */}
          <section id="inbox" className="doc-section">
            <h2>{t("💬 Using the Inbox")}</h2>
            <div className="doc-step">
              <h3>{t("Overview")}</h3>
              <p>
                {t(
                  "The Inbox is where you monitor and respond to all customer conversations. It provides real-time updates and comprehensive chat management."
                )}
              </p>
            </div>

            <div className="doc-step">
              <h3>{t("Chat List")}</h3>
              <p>{t("Left panel shows all your conversations:")}</p>
              <ul>
                <li>
                  <strong>{t("Agent Selection")}:</strong>{" "}
                  {t("Use the dropdown to filter chats by specific agents")}
                </li>
                <li>
                  <strong>{t("Chat Status")}:</strong>{" "}
                  {t("Green dot indicates active chats, gray indicates archived")}
                </li>
                <li>
                  <strong>{t("Last Message")}:</strong>{" "}
                  {t("Shows the most recent message and timestamp")}
                </li>
                <li>
                  <strong>{t("Real-time Updates")}:</strong>{" "}
                  {t("New messages appear automatically without refreshing")}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Chat Interface")}</h3>
              <p>{t("Right panel shows the selected conversation:")}</p>
              <ul>
                <li>
                  <strong>{t("Message History")}:</strong>{" "}
                  {t("Complete conversation history with timestamps")}
                </li>
                <li>
                  <strong>{t("AI Responses")}:</strong>{" "}
                  {t("Messages from your AI agent are clearly marked")}
                </li>
                <li>
                  <strong>{t("Admin Messages")}:</strong>{" "}
                  {t("Your responses appear in a different style")}
                </li>
                <li>
                  <strong>{t("Auto-scroll")}:</strong>{" "}
                  {t("Chat automatically scrolls to show the latest messages")}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Chat Management")}</h3>
              <p>{t("Control and organize your conversations:")}</p>
              <ul>
                <li>
                  <strong>{t("Send Messages")}:</strong>{" "}
                  {t("Type in the input field and click the send button (airplane icon)")}
                </li>
                <li>
                  <strong>{t("Archive Chats")}:</strong>{" "}
                  {t('Click "Archive" to move completed conversations out of the active list')}
                </li>
                <li>
                  <strong>{t("Disable AI Responses")}:</strong>{" "}
                  {t("Toggle to prevent AI from responding while you handle the conversation manually")}
                </li>
                <li>
                  <strong>{t("Search")}:</strong>{" "}
                  {t("Use the search function to find specific conversations or messages")}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Best Practices")}</h3>
              <ul>
                <li>
                  <strong>{t("Regular Monitoring")}:</strong>{" "}
                  {t("Check your inbox regularly to respond to messages that need human intervention")}
                </li>
                <li>
                  <strong>{t("Quick Responses")}:</strong>{" "}
                  {t("Respond promptly to maintain good customer service")}
                </li>
                <li>
                  <strong>{t("Archive Completed")}:</strong>{" "}
                  {t("Archive finished conversations to keep your inbox organized")}
                </li>
                <li>
                  <strong>{t("AI Training")}:</strong>{" "}
                  {t("Review AI responses and adjust agent prompts if needed")}
                </li>
              </ul>
            </div>
          </section>

          {/* Installing the Chat Widget */}
          <section id="installing-widget" className="doc-section">
            <h2>{t("🔧 Installing the Chat Widget")}</h2>
            <p>
              {t(
                'To generate your installation script, go to the Agents page and click "View Script" next to your agent. Then follow the instructions below to place it on your site.'
              )}{" "}
              <a href="/agents" target="_blank" rel="noreferrer">
                {t("Open the Agents page in a new tab →")}
              </a>
            </p>
            <div className="doc-step">
              <h3>{t("Getting Your Script")}</h3>
              <p>{t("After creating your agent, generate the installation script:")}</p>
              <ul>
                <li>{t("Go to the Agents tab")}</li>
                <li>{t('Click "View Script" next to your agent')}</li>
                <li>{t("Copy the generated JavaScript code")}</li>
                <li>{t("The script is unique to each agent and includes all configurations")}</li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Installation Requirements")}</h3>
              <ul>
                <li>
                  <strong>{t("Agent Must Be Active")}:</strong>{" "}
                  {t("Only active agents will respond to messages")}
                </li>
                <li>
                  <strong>{t("Domain Match")}:</strong>{" "}
                  {t("The domain in your agent settings must match your website")}
                </li>
                <li>
                  <strong>{t("Script Placement")}:</strong>{" "}
                  {t("Install the script before the closing </body> tag")}
                </li>
                <li>
                  <strong>{t("One Script Per Page")}:</strong>{" "}
                  {t("Don't install multiple scripts on the same page")}
                </li>
              </ul>
            </div>
          </section>

          {/* Platform-Specific Installation */}
          <section id="platforms" className="doc-section">
            <h2>{t("🌐 Platform-Specific Installation")}</h2>

            {/* WordPress */}
            <div className="cms-instruction">
              <h3>{t("📝 WordPress")}</h3>
              <div className="installation-steps">
                <h4>{t("Recommended: Simple Custom CSS and JS Plugin")}</h4>
                <ol>
                  <li>{t("Go to Plugins → Add New in your WordPress admin")}</li>
                  <li>{t('Search for "Simple Custom CSS and JS" by SilkyPress')}</li>
                  <li>{t("Install and activate the plugin")}</li>
                  <li>{t("Go to Appearance → Custom CSS & JS")}</li>
                  <li>{t('Click "Add Custom JS" button')}</li>
                  <li>{t("Paste your Supchats script in the code editor")}</li>
                  <li>{t("Set location to Footer (before </body> tag)")}</li>
                  <li>{t('Click "Publish" to save')}</li>
                </ol>
                <p>
                  <strong>{t("Why this plugin?")}</strong>{" "}
                  {t(
                    "Simple Custom CSS and JS is lightweight, user-friendly, and specifically designed for adding custom scripts to WordPress sites. It provides a clean interface and ensures proper script placement without affecting theme files."
                  )}
                </p>

                <h4>{t("Alternative: Theme Editor")}</h4>
                <ol>
                  <li>{t("Go to Appearance → Theme Editor")}</li>
                  <li>{t("Select footer.php")}</li>
                  <li>{t("Paste script before </body> tag")}</li>
                  <li>{t('Click "Update File"')}</li>
                </ol>
                <p>
                  <strong>{t("Note:")}</strong>{" "}
                  {t(
                    "Theme editor method will lose changes when you update your theme. The plugin method is recommended for permanent installation."
                  )}
                </p>
              </div>
            </div>

            {/* Joomla */}
            <div className="cms-instruction">
              <h3>{t("🔧 Joomla")}</h3>
              <div className="installation-steps">
                <h4>{t("Template Editor Method")}</h4>
                <ol>
                  <li>{t("Go to System → Site Templates")}</li>
                  <li>{t("Click your active template → index.php")}</li>
                  <li>{t("Find </body> tag")}</li>
                  <li>{t("Paste script just before it")}</li>
                  <li>{t("Save the file")}</li>
                </ol>
              </div>
            </div>

            {/* Tilda */}
            <div className="cms-instruction">
              <h3>{t("🎨 Tilda")}</h3>
              <div className="installation-steps">
                <h4>{t("Page Settings Method")}</h4>
                <ol>
                  <li>{t("Open page in Tilda editor")}</li>
                  <li>{t('Click "Page Settings" (gear icon)')}</li>
                  <li>{t("Go to SEO & Code tab")}</li>
                  <li>{t('Paste script in "HTML code before <head>" section')}</li>
                  <li>{t('Click "Save" and publish')}</li>
                </ol>
              </div>
            </div>

            {/* Default HTML */}
            <div className="cms-instruction">
              <h3>{t("🌐 HTML/Static Sites")}</h3>
              <div className="installation-steps">
                <h4>{t("Direct Integration")}</h4>
                <ol>
                  <li>{t("Open your HTML file in a text editor")}</li>
                  <li>{t("Find closing </body> tag")}</li>
                  <li>{t("Paste script just before it")}</li>
                  <li>{t("Save and upload to server")}</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Testing & Troubleshooting */}
          <section id="testing" className="doc-section">
            <h2>{t("✅ Testing & Troubleshooting")}</h2>
            <div className="doc-step">
              <h3>{t("Testing Your Installation")}</h3>
              <ul>
                <li>{t("Visit your website and check if the chat widget appears")}</li>
                <li>{t("Test the welcome message and initial interaction")}</li>
                <li>{t("Verify that the AI responds appropriately")}</li>
                <li>{t("Check mobile responsiveness")}</li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Common Issues")}</h3>
              <ul>
                <li>
                  <strong>{t("Widget not appearing")}:</strong>{" "}
                  {t("Check if the script is properly installed and the agent is active")}
                </li>
                <li>
                  <strong>{t("AI not responding")}:</strong>{" "}
                  {t(
                    "Verify your agent is not blocked and message limits haven't been exceeded"
                  )}
                </li>
                <li>
                  <strong>{t("Script errors")}:</strong>{" "}
                  {t("Ensure the script is placed before the closing </body> tag")}
                </li>
                <li>
                  <strong>{t("Domain issues")}:</strong>{" "}
                  {t("Make sure the domain in your agent settings matches your website")}
                </li>
              </ul>
            </div>

            <div className="doc-step">
              <h3>{t("Getting Help")}</h3>
              <ul>
                <li>{t("Check the News tab for updates and announcements")}</li>
                <li>{t("Contact support through your account settings")}</li>
                <li>{t("Review your agent configuration in the Agents tab")}</li>
                <li>{t("Monitor your usage in the Payment tab")}</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

